import React, { useMemo } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { ChatMessage, SentMessage as ISentMessage } from '@market-up/chat-core'
import Linkify from 'linkify-react'

const isImageMessage = (message: ChatMessage) => message.type === 'IMG_CHAT'
const isFileMessage = (message: ChatMessage) => message.type === 'FILE_CHAT'
const previewImage = (src: string) => window.open(src, '_blank')

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  & + & {
    margin-top: 20px;
  }
`
const CompanyMessageContainer = styled(MessageContainer)`
  && {
    width: max-content;
    padding-left: 40px;
  }
`
const AccountMessageContainer = styled(MessageContainer)`
  && {
    align-items: flex-end;
  }
`
const CompanyAvatar = styled.img.attrs(({ theme }) => ({
    src: theme.logo,
    alt: '头像'
}))`
  && {
    position: absolute;
    top: 0;
    left: 0;
    width: 28px;
    height: 28px;
  }
`

const BaseMessage = styled.div<{ border?: boolean }>`
  min-height: 1px;
  flex: none;
  display: inline-block;
  max-width: 250px;
  padding: ${props => props.border ? 0 : '7px 12px'};
  border: ${props => props.border ? '1px solid rgba(0,0,0,0.12)' : 'none'};
  border-radius: 10px;
  font-size: 14px;
  word-break: break-word;
  overflow: hidden;
`
const CompanyMessage = styled(BaseMessage)`
  border-top-left-radius: 0;
  background: #ECEDF4;
`
const AccountMessage = styled(BaseMessage)`
  position: relative;
  border-top-right-radius: 0;
  color: #fff;
  background-color: ${({ border, theme }) => border ? '#ECEDF4' : theme.themeColor};
  background-image: ${({ border, theme }) => border ? 'none' : theme.backgroundImage};
`
const MessageImage = styled.img.attrs(props => ({
    alt: '聊天图片',
    onClick: () => previewImage(props.src ?? '')
}))`
  max-width: 120px;
`
const MessageTime = styled.div.attrs<{ time: string | Date }>(({ time }) => ({
    children: dayjs(time).format('HH:mm')
}))<{ time: string | Date }>`
  && {
    flex: none;
    color: #8D8D8D;
    font-size: 12px;
  }
`
export interface MessageProps {
  message: ChatMessage
  resendMessage: (message: ChatMessage) => void
}
const isAccountMessage = (message: ChatMessage): boolean => {
  try {
    return JSON.parse(message.extValue).userType === 'account'
  } catch (e) {
    return true // all messages received from the server should have an extValue field containing a valid JSON string
  }
}
export const Message: React.FC<MessageProps> = ({ message, resendMessage }) => {
    if (!message.content) {
      return null
    }
    const isImage = isImageMessage(message)
    if (isAccountMessage(message)) {
        const content = isImage
            ? <MessageImage src={message.content} />
            : <Content content={message.content} />
        const resend = () => {
          if (message.status === 'timeout') {
            resendMessage(message)
          }
        }
        return (
            <AccountMessageContainer>
                <AccountMessage border={isImage}>
                  <Indicator status={message.status} onClick={resend} />
                    {content}
                </AccountMessage>
                <MessageTime time={message.sendTime} />
            </AccountMessageContainer>
        )
    } else {
        let content: React.ReactNode
        if (isImage) {
            content = <MessageImage src={message.content} />
        } else if (isFileMessage(message)) {
            const fileName = /^.*\/([^\/]*)$/.exec(message.content)?.[1] ?? '未知文件'
            content = <a href={message.content} download={true}>【文件】 {fileName}</a>
        } else {
            content = <Content content={message.content} />
        }
        return (
            <CompanyMessageContainer>
                <CompanyAvatar />
                <CompanyMessage border={isImage}>
                    {content}
                </CompanyMessage>
                <MessageTime time={message.sendTime} />
            </CompanyMessageContainer>
        )
    }
}
export interface RobotMessageProps {
    robotMsg: string
    show: boolean
}
export const RobotMessage: React.FC<RobotMessageProps> = ({ robotMsg, show }) => {
  return show
    ? (
        <CompanyMessageContainer>
          <CompanyAvatar />
          <CompanyMessage>
            {robotMsg}
          </CompanyMessage>
        </CompanyMessageContainer>
      )
    : null
}

const IndicatorContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform: translateX(calc(-100% - 8px));
  font-size: 12px;
  span {
    word-break: keep-all;
  }
  .clickable {
    cursor: pointer;
  }
`
interface IndicatorProps {
  status: ISentMessage['status']
  onClick?: () => void
}
const Indicator: React.VFC<IndicatorProps> = ({ status, onClick }) => {
  const element = useMemo(() => {
    switch (status) {
      case 'read': {
        return null
      }
      case 'sent': {
        return <span style={{ color: 'gray' }}>发送中</span>
      }
      case 'timeout': {
        return <span style={{ color: 'red', cursor: 'pointer' }} onClick={onClick}>发送失败</span>
      }
      case 'received': {
        return null
        // return <span style={{ color: 'gray' }}>未读</span>
      }
      default: {
        return null
      }
    }
  }, [status, onClick])
  return (
    <IndicatorContainer>{element}</IndicatorContainer>
  )
}

interface ContentProps {
  content?: string
}
const Content: React.VFC<ContentProps> = ({ content }) => {
  return (
    <Linkify tagName="div" options={{ defaultProtocol: 'http', target: '_blank' }}>
      {content ?? ''}
    </Linkify>
  )
}
